var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { AvatarContext, CounterAvatar } from './utils';
import MuiAvatarGroup from '@mui/material/AvatarGroup/AvatarGroup';
import React, { Children, useEffect, useLayoutEffect, useRef, useState, } from 'react';
/**
 * Displays children only up to maximum, and adds CounterAvatar to represent the remainder
 * @param children see determineRender
 * @param max number of children to be displayed
 * @param overMax number of remaining children
 */
var hideAboveMax = function (children, max, overMax) {
    var avatarArray = Children.toArray(children).slice(0, max - 1);
    avatarArray.push(React.createElement(CounterAvatar, { key: overMax.toString(), data: overMax.toString() }));
    return avatarArray;
};
/**
 * Displays all defined children, and adds CounterAvatar to represent the unspecified remainder
 * @param children see determineRender
 * @param overMax difference between total displayed number and the actual number of children
 */
var showRemainingTotal = function (children, overMax) {
    var avatarArray = Children.toArray(children);
    avatarArray.push(React.createElement(CounterAvatar, { key: overMax.toString(), data: overMax.toString() }));
    return avatarArray;
};
/**
 * Determines if the given number of children will fit in width of container
 * @param childrenLength number of children in AvatarGroup
 * @param width see determineRender
 */
var willGroupFit = function (childrenLength, width) {
    // 22 = space taken by stacked avatar
    // 8 = 8px for last avatar non-stacked space
    return 22 * childrenLength + 8 <= width;
};
/**
 * Determines how many children shall be rendered as Avatars, and how many will be grouped into a CounterAvatar
 * @param children of AvatarGroup; intended to only contain Avatars. Placing different elements *will* result in miscalculation!
 * @param max limit display of children to this number, incl. CounterAvatar
 * @param total if greater than number of children, the difference shall be displayed as a CounterAvatar
 * @param width bounding width of the AvatarGroup container
 */
var determineRender = function (children, max, total, width) {
    var childrenLength = Children.count(children);
    var willBeLimited = childrenLength > max && max > 0;
    var displayedChildrenLength = willBeLimited ? max : childrenLength;
    // the children group (possibly limited to max) will not fit the container
    if (!willGroupFit(displayedChildrenLength, width)) {
        var maxCanFit = Math.floor((width - 8) / 22); // maximum number of elements that can fit in container
        var totalLength = childrenLength > total ? childrenLength : total;
        return hideAboveMax(children, maxCanFit, totalLength - maxCanFit + 1);
    }
    // max is specified, and either children exceed it || they would exceed with the one extra element for specified remaining total
    if (willBeLimited ||
        (total > childrenLength && childrenLength + 1 > max && max > 0)) {
        var totalLength = childrenLength > total ? childrenLength : total;
        return hideAboveMax(children, max, totalLength - max + 1);
    }
    // defined children can all be displayed, and display the remainder of total
    else if (childrenLength < total) {
        return showRemainingTotal(children, total - childrenLength);
    }
    // no limitations, no remainder of total, so just display children as they are
    return children;
};
export var AvatarGroup = function (_a) {
    var children = _a.children, _b = _a.max, max = _b === void 0 ? 0 : _b, _c = _a.total, total = _c === void 0 ? Children.count(children) : _c, props = __rest(_a, ["children", "max", "total"]);
    var ref = useRef(null);
    // inferred width of the AvatarGroup, so we can hide Avatars that would overflow
    var _d = useState(0), width = _d[0], setWidth = _d[1];
    useLayoutEffect(function () {
        var _a;
        setWidth((_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.offsetWidth);
    }, []);
    useEffect(function () {
        function handleWindowResize() {
            var _a;
            setWidth((_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.offsetWidth);
        }
        window.addEventListener('resize', handleWindowResize);
        return function () {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);
    return (React.createElement(MuiAvatarGroup, __assign({ ref: ref }, props),
        React.createElement(AvatarContext.Provider, { value: true }, Children.count(children) > 0 &&
            determineRender(children, max, total, width))));
};
//# sourceMappingURL=AvatarGroup.js.map